import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { reportsAPI } from '../services/api';
import api from '../services/api';
import './AgeOutNextMonthBySCReport.css';

const REPORT_ID = 'age-out-next-month-by-sc';
const STORAGE_KEY = `canonical-report:${REPORT_ID}:state`;

function formatDateShort(str) {
  if (!str) return '—';
  const s = String(str).trim();
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${Number(match[2])}/${Number(match[3])}/${match[1]}`;
  try {
    const d = new Date(str);
    if (!Number.isNaN(d.getTime())) return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  } catch (_) {}
  return str;
}

function getInitialSort(definition) {
  const configured = definition?.dataset?.defaultSort;
  if (Array.isArray(configured) && configured.length > 0) return configured;
  return [{ key: 'staff_name', dir: 'asc' }];
}

function getInitialFilters(definition) {
  const { age3_date: _omit, ...rest } = definition?.defaults?.filters || {};
  return rest;
}

function readSavedState(tenantCode) {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if ((parsed.tenantCode || null) !== (tenantCode || null)) return null;
    return {
      filters: parsed.filters || null,
      sort: parsed.sort || null,
    };
  } catch (_) {
    return null;
  }
}

function summarizeFilterValue(column, value) {
  if (!column || !value) return null;
  const kind = column.filter?.kind;
  if (kind === 'textContains') {
    const text = (value.text || '').trim();
    return text || null;
  }
  if (kind === 'dateRange') {
    const from = value.from || '';
    const to = value.to || '';
    if (!from && !to) return null;
    const fromDisplay = from ? formatDateShort(from) : 'Any';
    const toDisplay = to ? formatDateShort(to) : 'Any';
    return `${fromDisplay} to ${toDisplay}`;
  }
  if (kind === 'multiSelect') {
    const values = Array.isArray(value.values) ? value.values : [];
    if (values.length === 0) return null;
    return values.join(', ');
  }
  return null;
}

function isFilterActive(column, value) {
  return Boolean(summarizeFilterValue(column, value));
}

function getDefaultMonth() {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function AgeOutNextMonthBySCReport({ embedded = false, refreshKey = 0, tenantCode = null }) {
  const [searchParams] = useSearchParams();
  const filterMe = searchParams.get('filter') === 'me';
  const [targetMonth, setTargetMonth] = useState(getDefaultMonth);

  const [definition, setDefinition] = useState(null);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState([]);
  const [rows, setRows] = useState([]);
  const [facets, setFacets] = useState({});
  const [total, setTotal] = useState(0);
  const [runAt, setRunAt] = useState(new Date());
  const [rangeText, setRangeText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [draftFilters, setDraftFilters] = useState({});
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, width: 190 });
  const filterButtonRefs = useRef({});
  // When ?filter=me, hold off running the query until whoami resolves and injects the staff filter
  const [queryReady, setQueryReady] = useState(!filterMe);

  const columns = definition?.columns || [];
  const visibleColumns = useMemo(() => columns.filter((c) => c.visibleByDefault !== false), [columns]);

  useEffect(() => {
    let mounted = true;
    reportsAPI
      .canonicalDefinition(REPORT_ID)
      .then((res) => {
        if (!mounted) return;
        const def = res.data?.data || null;
        setDefinition(def);
        const saved = readSavedState(tenantCode);
        // When ?filter=me, skip localStorage filters so the whoami effect sets the staff filter cleanly
        setFilters(filterMe ? getInitialFilters(def) : (saved?.filters || getInitialFilters(def)));
        setSort(saved?.sort || getInitialSort(def));
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err?.response?.data?.error || err?.message || 'Failed to load report definition');
      });
    return () => {
      mounted = false;
    };
  }, [tenantCode, refreshKey]); // filterMe is stable for the component lifetime

  useEffect(() => {
    setFilters((prev) => {
      if (!prev.age3_date) return prev;
      const next = { ...prev };
      delete next.age3_date;
      return next;
    });
  }, [targetMonth]);

  useEffect(() => {
    if (!definition || filterMe) return;
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ tenantCode: tenantCode || null, filters, sort })
      );
    } catch (_) {}
  }, [definition, filters, sort, tenantCode, filterMe]);

  // When ?filter=me: fetch whoami, inject staff_name filter, then allow the query to run
  useEffect(() => {
    if (!filterMe) return;
    api.get('/whoami').then((res) => {
      const name = res.data?.staff_display_name ?? null;
      if (name) {
        setFilters((prev) => ({ ...prev, staff_name: { values: [name] } }));
      }
    }).catch(() => {
      // proceed without filter if whoami fails
    }).finally(() => {
      setQueryReady(true);
    });
  }, []); // intentional: run once on mount only

  useEffect(() => {
    if (!definition || !queryReady) return;
    let mounted = true;
    setLoading(true);
    setError(null);
    reportsAPI
      .canonicalQuery(REPORT_ID, {
        filters,
        sort,
        page: { limit: 1000, offset: 0 },
        month: targetMonth,
      })
      .then((res) => {
        if (!mounted) return;
        const payload = res.data?.data || {};
        setRows(Array.isArray(payload.rows) ? payload.rows : []);
        setFacets(payload.facets || {});
        setTotal(Number(payload.total || 0));
        setRunAt(new Date());
        const firstDay = payload.first_day_next_month;
        const lastDay = payload.last_day_next_month;
        if (firstDay && lastDay) {
          setRangeText(`${formatDateShort(firstDay)} - ${formatDateShort(lastDay)}`);
        } else {
          setRangeText('');
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err?.response?.data?.error || err?.message || 'Failed to load report data');
        setRows([]);
        setFacets({});
        setTotal(0);
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [definition, filters, sort, refreshKey, queryReady, targetMonth]);

  useEffect(() => {
    if (!openFilterKey) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (target && target.closest && target.closest('.age-next-header-filter-wrap')) return;
      if (target && target.closest && target.closest('.age-next-filter-popover-portal')) return;
      setOpenFilterKey(null);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpenFilterKey(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openFilterKey]);

  useEffect(() => {
    if (!openFilterKey) return undefined;
    const updatePosition = () => {
      const button = filterButtonRefs.current[openFilterKey];
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const isDateFilter = openFilterKey === 'age3_date' || openFilterKey === 'ifsp_date' || openFilterKey === 'birthdate';
      const width = isDateFilter ? 190 : 320;
      const viewportPadding = 8;
      const maxLeft = window.innerWidth - width - viewportPadding;
      const preferredLeft = rect.right + window.scrollX - width;
      const clampedLeft = Math.max(viewportPadding, Math.min(preferredLeft, maxLeft));
      setPopoverPosition({
        top: rect.bottom + window.scrollY + 6,
        left: clampedLeft,
        width,
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [openFilterKey]);

  const activeFilterSummary = useMemo(() => {
    return visibleColumns
      .map((col) => {
        const text = summarizeFilterValue(col, filters[col.key]);
        if (!text) return null;
        return `${col.label}: ${text}`;
      })
      .filter(Boolean);
  }, [visibleColumns, filters]);

  const handleSortClick = (key) => {
    setSort((prev) => {
      const current = Array.isArray(prev) && prev[0]?.key === key ? prev[0] : null;
      if (!current) return [{ key, dir: 'asc' }];
      if (current.dir === 'asc') return [{ key, dir: 'desc' }];
      return [{ key: 'staff_name', dir: 'asc' }];
    });
  };

  const handleClearFilters = () => {
    if (!definition) return;
    setFilters(getInitialFilters(definition));
    setSort(getInitialSort(definition));
  };

  const handleExport = async () => {
    try {
      const response = await reportsAPI.canonicalExportCsv(REPORT_ID, { filters, sort, month: targetMonth });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'age_out_next_month_by_sc.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to export CSV');
    }
  };

  const renderFilterControl = (column) => {
    const kind = column?.filter?.kind;
    const value = draftFilters[column.key] || {};
    if (kind === 'textContains') {
      return (
        <input
          type="text"
          value={value.text || ''}
          onChange={(e) =>
            setDraftFilters((prev) => ({
              ...prev,
              [column.key]: { text: e.target.value },
            }))
          }
          placeholder={`Search ${column.label.toLowerCase()}`}
          className="age-next-filter-input"
        />
      );
    }
    if (kind === 'dateRange') {
      return (
        <div className="age-next-filter-date-range">
          <label className="age-next-filter-date-field">
            <span>From</span>
            <input
              type="date"
              value={value.from || ''}
              onChange={(e) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  [column.key]: { ...value, from: e.target.value },
                }))
              }
              className="age-next-filter-input"
            />
          </label>
          <label className="age-next-filter-date-field">
            <span>To</span>
            <input
              type="date"
              value={value.to || ''}
              onChange={(e) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  [column.key]: { ...value, to: e.target.value },
                }))
              }
              className="age-next-filter-input"
            />
          </label>
        </div>
      );
    }
    if (kind === 'multiSelect') {
      const options = Array.isArray(facets[column.key]) ? facets[column.key] : [];
      const selectedValues = Array.isArray(value.values) ? value.values : [];
      return (
        <select
          multiple
          value={selectedValues}
          onChange={(e) => {
            const values = Array.from(e.target.selectedOptions).map((opt) => opt.value);
            setDraftFilters((prev) => ({
              ...prev,
              [column.key]: { values },
            }));
          }}
          className="age-next-filter-select"
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }
    return null;
  };

  const openFilter = (key, buttonElement) => {
    setDraftFilters((prev) => ({
      ...prev,
      [key]: filters[key] || {},
    }));
    if (buttonElement) {
      filterButtonRefs.current[key] = buttonElement;
    }
    setOpenFilterKey((prev) => (prev === key ? null : key));
  };

  const applyFilter = (column) => {
    const key = column.key;
    const kind = column.filter?.kind;
    const draft = draftFilters[key] || {};
    const nextFilters = { ...filters };
    if (kind === 'textContains') {
      const text = String(draft.text || '').trim();
      if (text) nextFilters[key] = { text };
      else delete nextFilters[key];
    } else if (kind === 'dateRange') {
      const from = draft.from || '';
      const to = draft.to || '';
      if (from || to) nextFilters[key] = { from, to };
      else delete nextFilters[key];
    } else if (kind === 'multiSelect') {
      const values = Array.isArray(draft.values) ? draft.values.filter(Boolean) : [];
      if (values.length > 0) nextFilters[key] = { values };
      else delete nextFilters[key];
    }
    setFilters(nextFilters);
    setOpenFilterKey(null);
  };

  const clearFilter = (column) => {
    const key = column.key;
    setFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setDraftFilters((prev) => ({ ...prev, [key]: {} }));
    setOpenFilterKey(null);
  };

  const openColumn = visibleColumns.find((c) => c.key === openFilterKey);
  const filterPopover = openColumn
    ? createPortal(
        <div
          className="age-next-filter-popover age-next-filter-popover-portal"
          style={{
            top: `${popoverPosition.top}px`,
            left: `${popoverPosition.left}px`,
            width: `${popoverPosition.width}px`,
          }}
        >
          {renderFilterControl(openColumn)}
          <div className="age-next-filter-popover-actions">
            <button type="button" className="btn btn-secondary" onClick={() => clearFilter(openColumn)}>
              Clear
            </button>
            <button type="button" className="btn btn-primary" onClick={() => applyFilter(openColumn)}>
              Apply
            </button>
          </div>
        </div>,
        document.body
      )
    : null;

  const titleSuffix = tenantCode ? ` - ${tenantCode}` : '';
  const sortKey = sort?.[0]?.key;
  const sortDir = sort?.[0]?.dir;
  const runAtDisplay = runAt.toLocaleString();

  return (
    <>
      <div className={`age-out-next-month-report ${embedded ? 'age-next-embedded' : ''}`}>
        <div className="age-next-toolbar no-print">
          <div className="age-next-toolbar-left">
            <h3 className="age-next-title">Age Out Next Month by SC{titleSuffix}</h3>
            <div className="age-next-subtitle">{loading ? 'Loading...' : `${total} row${total === 1 ? '' : 's'}`}</div>
          </div>
          <div className="age-next-toolbar-actions">
            <label className="age-next-month-picker-label">
              Month:
              <input
                type="month"
                value={targetMonth}
                onChange={(e) => e.target.value && setTargetMonth(e.target.value)}
                className="age-next-month-picker"
              />
            </label>
            <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
              Reset
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleExport}>
              Export CSV
            </button>
            <button type="button" className="btn btn-primary" onClick={() => window.print()}>
              Print
            </button>
          </div>
        </div>

        {rangeText && <p className="age-next-range no-print">Third birthday in: {rangeText}</p>}
        {activeFilterSummary.length > 0 && (
          <p className="age-next-filter-summary no-print">Filters: {activeFilterSummary.join(' | ')}</p>
        )}
        <div className="age-next-print-header print-only">
          <h1>Age Out Next Month by SC{titleSuffix}</h1>
        </div>

        {error && <div className="age-next-error">{error}</div>}

        <div className="age-next-table-wrap">
          <table className="age-next-table">
            <thead>
              <tr>
                {visibleColumns.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => col.sortable && handleSortClick(col.key)}
                    className={col.sortable ? 'age-next-sortable' : ''}
                  >
                    <div className="age-next-header-cell">
                      <span className="age-next-header-label">
                        {col.label}
                        {sortKey === col.key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                      </span>
                      {col.filter && (
                        <div className="age-next-header-filter-wrap" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className={`age-next-filter-trigger ${isFilterActive(col, filters[col.key]) ? 'is-active' : ''}`}
                            onClick={(e) => openFilter(col.key, e.currentTarget)}
                            aria-label={`Filter ${col.label}`}
                          >
                            <svg
                              className="age-next-filter-trigger-icon"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-hidden="true"
                            >
                              <path
                                d="M4 6H20L14 13V18L10 20V13L4 6Z"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={visibleColumns.length}>Loading...</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length}>No open clients with 3rd birthday in the next month.</td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.client_id}>
                    {visibleColumns.map((col) => {
                      const raw = row[col.key];
                      const display = col.type === 'date' ? formatDateShort(raw) : raw ?? '—';
                      return <td key={`${row.client_id}-${col.key}`}>{display}</td>;
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="age-next-print-footer print-only">
          <p>Run at: {runAtDisplay}</p>
          <p>Range: {rangeText || 'Next month'}</p>
          <p>Filters: {activeFilterSummary.length > 0 ? activeFilterSummary.join(' | ') : 'None'}</p>
        </div>
      </div>
      {filterPopover}
    </>
  );
}

export default AgeOutNextMonthBySCReport;
